import { useCallback } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Option } from '@common-fe/common-fe';
import _ from 'lodash';

import { useSnackbar } from '@/modules/core/hooks';

import { PlanTypes } from '../enrollmentModal.types';

import { EnrollmentFormField, EnrollmentFormFields } from './useEnrollmentForm';
import { useGetDefaultCoverageTierDataFromBackend } from './useGetDefaultCoverageTierDataFromBackend';

export interface DefaultCoverageTierDataFromBackend {
  coverage_level?: string;
  coverage_level_effective_date?: string;
}

const SSN_REQUIRED_ERROR_MESSAGE =
  'SSN is required to enroll in an HSA. Participant does not have an SSN. Add an SSN then reprocess the HSA enrollment.';
const SSN_REQUIRED_FIELD_MESSAGE = 'Add SSN for this participant to enroll in HSA';
const INVALID_DATE_ERROR_MESSAGE = 'Invalid date';
const HEALTH_PLAN_CONFLICT_MESSAGE = "You're already enrolled in a health plan for this date";
const PLAN_CONFLICT_MESSAGE = "You're already enrolled in a plan for this date";
const ENROLLMENT_CONFLICT_ERROR_CODE = '40900';

interface UseEnrollmentSubmitParams {
  planType?: PlanTypes;
  healthPlansOptions: Option[];
  plansOptions: Option[];
  onSave: (
    formValues: EnrollmentFormFields,
    defaultCoverageTierDataFromBackend?: DefaultCoverageTierDataFromBackend
  ) => Promise<void>;
  onSaveHealthPlanEnrollment: (formValues: EnrollmentFormFields) => Promise<void>;
  isEdit: boolean;
  currentSetModalVisible: (visible: boolean) => void;
  methods: UseFormReturn<EnrollmentFormFields>;
  setErrorModal: (visible: boolean) => void;
  contributionMonitoringRequired?: boolean;
  onConflict?: (message: string) => void;
}

const useEnrollmentSubmit = (params: UseEnrollmentSubmitParams) => {
  const {
    planType,
    healthPlansOptions,
    plansOptions,
    onSave,
    onSaveHealthPlanEnrollment,
    isEdit,
    currentSetModalVisible,
    methods,
    setErrorModal,
    contributionMonitoringRequired,
  } = params;

  const { handleAddPermanentSnackbar } = useSnackbar();
  const { defaultCoverageTierDataFromBackend } = useGetDefaultCoverageTierDataFromBackend(
    methods.watch().plan,
    contributionMonitoringRequired
  );

  return useCallback(
    async (formValues: EnrollmentFormFields) => {
      const editPlanName =
        (planType === PlanTypes.HEALTH
          ? healthPlansOptions?.find((item) => item.key === formValues.healthPlan)
          : plansOptions?.find((item) => item.key === formValues.plan)
        )?.title || '';
      try {
        if (formValues) {
          if (formValues.type === PlanTypes.HEALTH) {
            await onSaveHealthPlanEnrollment(formValues);
          } else {
            await onSave(formValues, defaultCoverageTierDataFromBackend);
          }
          if (isEdit) {
            handleAddPermanentSnackbar({
              text: `${editPlanName} modified successfully.`,
              closeIcon: true,
            });
            currentSetModalVisible(false);
          } else {
            handleAddPermanentSnackbar({
              text: 'Enrollment updates are processing. Refresh the page to see',
              subtext: 'updates. This may take a few moments to complete. ',
              closeIcon: true,
              width: '458px',
            });
            currentSetModalVisible(false);
          }
        }
      } catch (e) {
        const message = _.get(e, 'response.data.elevate_error_message', '') || '';
        const errorCode = _.get(e, 'response.data.elevate_error_code', '') as string;
        if (message.includes(SSN_REQUIRED_ERROR_MESSAGE)) {
          methods.setError(
            formValues?.type === PlanTypes.HEALTH ? EnrollmentFormField.healthPlan : EnrollmentFormField.plan,
            {
              type: 'server',
              message: SSN_REQUIRED_FIELD_MESSAGE,
            }
          );
          return;
        }
        if (errorCode === ENROLLMENT_CONFLICT_ERROR_CODE) {
          const isHealthPlan = formValues?.type === PlanTypes.HEALTH;
          methods.setError(
            isHealthPlan ? EnrollmentFormField.healthPlanEnrollmentDate : EnrollmentFormField.enrollmentDate,
            {
              type: 'server',
              message: INVALID_DATE_ERROR_MESSAGE,
            }
          );
          params.onConflict?.(isHealthPlan ? HEALTH_PLAN_CONFLICT_MESSAGE : PLAN_CONFLICT_MESSAGE);
          return;
        }
        setErrorModal(true);
      }
    },
    [
      defaultCoverageTierDataFromBackend,
      healthPlansOptions,
      isEdit,
      methods,
      onSave,
      onSaveHealthPlanEnrollment,
      currentSetModalVisible,
      planType,
      plansOptions,
      setErrorModal,
      handleAddPermanentSnackbar,
      params,
    ]
  );
};

export default useEnrollmentSubmit;
